"use client";

import { useId, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Field, FieldLabel } from "@/components/ui/field";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { Input } from "@/components/ui/input";
import { trackToolUsed } from "@/lib/analytics/tool-events";
import {
  VERDICT_TAG,
  formatMoney,
  isImplausiblePrice,
  medianComparison,
  scopeName,
  verdictFor,
  verdictHeadline,
  verdictTone,
  wholeNumber,
  yearlyDifferenceCents,
  type CheckSide,
  type MarketBand,
} from "./price-check-model";

export type TradeHrefs = { buyHref: string; sellHref: string };

function MarketStats({ band }: { band: MarketBand }) {
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <dt className="text-fg-muted">{scopeName(band)} median</dt>
        <dd className="tabular-nums text-lg font-semibold text-foreground">
          {formatMoney(band.medianCents, band.currency)}
        </dd>
      </div>
      <div>
        <dt className="text-fg-muted">Middle half</dt>
        <dd className="tabular-nums text-lg font-semibold text-foreground">
          {formatMoney(band.lowCents, band.currency)}–{formatMoney(band.highCents, band.currency)}
        </dd>
      </div>
    </dl>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        variant="mono"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function YearlyImpact({
  band,
  priceCents,
  side,
}: {
  band: MarketBand;
  priceCents: number;
  side: CheckSide;
}) {
  const [unitsPerLoad, setUnitsPerLoad] = useState("500");
  const [loadsPerMonth, setLoadsPerMonth] = useState("4");
  const differenceCents = yearlyDifferenceCents(
    band,
    priceCents,
    wholeNumber(unitsPerLoad),
    wholeNumber(loadsPerMonth),
  );
  const amount = formatMoney(Math.abs(differenceCents), band.currency);
  const atMedian = side === "buy" ? "paying the median" : "selling at the median";
  const summary =
    differenceCents === 0
      ? `That's the same as ${atMedian} over a year.`
      : `That's ${amount} a year ${differenceCents > 0 ? "more" : "less"} than ${atMedian}.`;
  return (
    <div className="grid gap-3 border-t border-border-subtle pt-4">
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Units per load" value={unitsPerLoad} onChange={setUnitsPerLoad} />
        <NumberField label="Loads per month" value={loadsPerMonth} onChange={setLoadsPerMonth} />
      </div>
      <p aria-live="polite" className="text-sm font-medium text-foreground">
        {summary}
      </p>
    </div>
  );
}

function TradeLinksRow({
  hrefs,
  side,
  productTypeSlug,
}: {
  hrefs: TradeHrefs;
  side: CheckSide;
  productTypeSlug: string;
}) {
  const track = () =>
    trackToolUsed({ tool: "price-check", tool_step: "cta_clicked", productTypeSlug });
  const linkClass = "text-sm font-semibold text-link hover:text-link-hover hover:underline";
  return (
    <p className="flex flex-wrap gap-x-5 gap-y-1">
      {side === "buy" ? (
        <HoverPrefetchLink href={hrefs.buyHref} onClick={track} className={linkClass}>
          See prices from sellers near you
        </HoverPrefetchLink>
      ) : (
        <HoverPrefetchLink href={hrefs.sellHref} onClick={track} className={linkClass}>
          Get an offer for yours
        </HoverPrefetchLink>
      )}
    </p>
  );
}

function VerdictSummary({
  band,
  priceCents,
  side,
}: {
  band: MarketBand;
  priceCents: number;
  side: CheckSide;
}) {
  const verdict = verdictFor(band, priceCents);
  return (
    <div className="grid gap-2">
      <Badge tone={verdictTone(verdict, side)} className="w-fit">
        {VERDICT_TAG[verdict]}
      </Badge>
      <p className="text-xl font-semibold text-foreground">
        {verdictHeadline(band, priceCents, side)}
      </p>
      <p className="text-sm text-fg-muted">{medianComparison(band, priceCents)}</p>
    </div>
  );
}

function CheckThisPrice({
  band,
  hrefs,
  side,
  productTypeSlug,
}: {
  band: MarketBand;
  hrefs: TradeHrefs;
  side: CheckSide;
  productTypeSlug: string;
}) {
  return (
    <div aria-live="polite" className="grid gap-4">
      <p className="text-xl font-semibold text-foreground">Check this price</p>
      <p className="text-sm text-fg-muted">
        It is far from the {scopeName(band)} median. Enter the price per unit.
      </p>
      <MarketStats band={band} />
      <TradeLinksRow hrefs={hrefs} side={side} productTypeSlug={productTypeSlug} />
    </div>
  );
}

export function PriceCheckVerdict({
  band,
  priceCents,
  side,
  isLoading,
  hrefs,
  productTypeSlug,
  showImpact = true,
}: {
  band: MarketBand | null;
  priceCents: number | null;
  side: CheckSide;
  isLoading: boolean;
  hrefs: TradeHrefs;
  productTypeSlug: string;
  showImpact?: boolean;
}) {
  if (!band) {
    return (
      <div aria-live="polite" className="grid gap-3">
        <p className="text-sm text-fg-muted">
          {isLoading ? "Reading the market…" : "No market price for this yet."}
        </p>
        <TradeLinksRow hrefs={hrefs} side={side} productTypeSlug={productTypeSlug} />
      </div>
    );
  }
  if (priceCents === null) {
    return (
      <div aria-live="polite" className="grid gap-4">
        <p className="text-sm text-fg-muted">Enter your price or drag the marker to compare it.</p>
        <MarketStats band={band} />
        <TradeLinksRow hrefs={hrefs} side={side} productTypeSlug={productTypeSlug} />
      </div>
    );
  }
  if (isImplausiblePrice(band, priceCents)) {
    return <CheckThisPrice band={band} hrefs={hrefs} side={side} productTypeSlug={productTypeSlug} />;
  }
  return (
    <div aria-live="polite" className="grid gap-4">
      <VerdictSummary band={band} priceCents={priceCents} side={side} />
      <MarketStats band={band} />
      {showImpact && <YearlyImpact band={band} priceCents={priceCents} side={side} />}
      <TradeLinksRow hrefs={hrefs} side={side} productTypeSlug={productTypeSlug} />
    </div>
  );
}
